import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  VStack,
  Text,
  Breadcrumb as ChakraBreadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Heading,
  AbsoluteCenter,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  Input,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { OtherSourceData } from "src/types";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { getOtherSources } from "src/api/sal-magic/query";
import { FREQUENCY_ROUTE, OTHER_SOURCES_ROUTE, WAVES_ROUTE } from "src/routePaths";
import SalMagicGate from "./components/SalMagicGate";
import SalMagicAudioPlayer from "./components/SalMagicPlayer/SalMagicAudioPlayer";
import { Link as ReactRouterLink } from "react-router-dom";
import SectionHeader from "src/components/SectionHeader/SectionHeader";
import { useSelector } from "src/store";
import FreeTrialLock from "src/components/FreeTrialLock";
import { useCheckSubscription } from "src/free-trial/useCheckSubscriptions";
import { useFreeTrial } from "src/free-trial/helpers";

const OtherSources = () => {
  const {
    data: otherSourcesData,
    isLoading: isOtherSourcesLoading,
    error,
  } = useQuery<OtherSourceData, Error>(
    ["listOtherSources", localStorage.getItem("locale")],
    getOtherSources,
    {
      enabled: true,
      retry: 3,
      retryDelay: 1000,
      refetchOnWindowFocus: false,
    }
  );

  const { isTrialUser: isFreeTrial } = useFreeTrial(0);

  const { formatMessage } = useIntl();

  // State to hold durations keyed by track URL or track ID
  const [durations, setDurations] = useState<Record<string, number>>({});

  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (otherSourcesData) {
      // Extract all track URLs from the data
      const allTracks = otherSourcesData.flatMap(
        (category) => category.other_sources
      );

      // Create a copy of durations to not mutate state directly
      const newDurations = { ...durations };

      // A helper function to load the duration for a single track
      const loadDuration = (trackUrl: string, trackId: number) => {
        return new Promise<void>((resolve) => {
          const audio = new Audio();
          audio.preload = "metadata";
          audio.src = trackUrl;
          audio.addEventListener("loadedmetadata", () => {
            newDurations[trackId] = audio.duration;
            resolve();
          });
          audio.addEventListener("error", () => {
            // If there's an error, set duration to 0 or handle accordingly
            newDurations[trackId] = 0;
            resolve();
          });
        });
      };

      // Load all durations and once done, update state
      Promise.all(allTracks.map((t) => loadDuration(t.url, t.id))).then(() => {
        setDurations(newDurations);
      });
    }
  }, [otherSourcesData]);

  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value.toLowerCase());
  };

  if (isOtherSourcesLoading) {
    return (
      <AbsoluteCenter>
        <Spinner aria-label='loading...' size='xl' />
      </AbsoluteCenter>
    );
  }

  if (error) {
    return (
      <Box my={10}>
        <Alert status='error'>
          <AlertIcon />
          <AlertTitle mr={2}>Error loading data</AlertTitle>
          <AlertDescription>
            <FormattedMessage
              id='dataLoadError'
              defaultMessage='Unable to load the data. Please try again later.'
            />
          </AlertDescription>
          <CloseButton position='absolute' right='8px' top='8px' />
        </Alert>
      </Box>
    );
  }

  return (
    <Box my={10}>
      <ChakraBreadcrumb
        color='gray.900'
        textTransform='capitalize'
        separator=' '>
        <BreadcrumbItem w='100%'>
          <>
            <BreadcrumbLink as={ReactRouterLink} to={FREQUENCY_ROUTE}>
              <FormattedMessage id='frequency' />
            </BreadcrumbLink>
            &nbsp;-&nbsp;
            <BreadcrumbLink as={ReactRouterLink} to={WAVES_ROUTE}>
              <FormattedMessage id='waves' />
            </BreadcrumbLink>
            &nbsp;-&nbsp;
            <BreadcrumbLink as={ReactRouterLink} to={OTHER_SOURCES_ROUTE}>
              <FormattedMessage id='Other Sources' />
            </BreadcrumbLink>
          </>
        </BreadcrumbItem>

        <BreadcrumbItem
          isCurrentPage
          w='100%'
          justifyContent='space-between'
          alignItems='start'>
          <SectionHeader slug='other_sources' />
          <SalMagicGate radius={105} />
        </BreadcrumbItem>
      </ChakraBreadcrumb>
      <Input
        type='text'
        placeholder={formatMessage({ id: "searchOtherSources" })}
        maxWidth='300px'
        variant={"flushed"}
        my='6'
        onChange={changeHandler}
      />
      <VStack
        display={"flex"}
        wrap={"wrap"}
        margin={"50px 0"}
        width='100%'
        flexDir='row'
        justifyContent='center'
        alignItems='center'>
        {searchValue === "" ? (
          <Accordion allowMultiple w='100%'>
            {otherSourcesData
              ?.sort((a, b) => a.id - b.id)
              .map((category) => (
                <span id={`category-${category.id}`}>
                  <AccordionItem
                    key={`item-${category.id}`}
                    id={`category-${category.id}`}>
                    <h2>
                      <AccordionButton key={`button-${category.id}`}>
                        <Text
                          flex='1'
                          textAlign='left'
                          fontWeight='bold'
                          fontSize={18}>
                          {category.name}
                        </Text>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4} px={10}>
                      <VStack width='100%' spacing={6}>
                        {category.other_sources
                          .sort((a, b) => a.id - b.id)
                          .map((source, index) => (
                            <VStack
                              key={`${index}:vstack-other-source${source.id}`}
                              width='100%'>
                              <FreeTrialLock isLocked={isFreeTrial} fullWidth>
                                <SalMagicAudioPlayer
                                  key={`${index}:other-source-${source.id}`}
                                  entityName={"other_sources"}
                                  trackUrl={source.url}
                                  trackName={source.name}
                                  trackCategory={category.name}
                                  trackInfo={source.description}
                                  trackId={source.id}
                                  trackCover={source.imageUrl}
                                  categoryId={category.id.toString()}
                                  // Pass the preloaded duration here if available
                                  preloadedDuration={durations[source.id] ?? 0}
                                />
                              </FreeTrialLock>
                              {index < category.other_sources.length - 1 && (
                                <hr
                                  style={{
                                    margin: "36px",
                                    height: "2px",
                                    width: "80%",
                                  }}
                                />
                              )}
                            </VStack>
                          ))}
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                </span>
              ))}
          </Accordion>
        ) : (
          <VStack width='100%' spacing={6}>
            {otherSourcesData &&
              otherSourcesData.flatMap((category, categoryIndex) =>
                category.other_sources
                  .filter(
                    (source) =>
                      source.name?.toLowerCase().includes(searchValue) ||
                      source.description?.toLowerCase().includes(searchValue)
                  )
                  .map((source, index) => (
                    <VStack
                      key={`${index}:vstack-other-source${source.id}`}
                      width='100%'>
                      <FreeTrialLock isLocked={isFreeTrial} fullWidth>
                        <SalMagicAudioPlayer
                          key={`${index}:other-source-${source.id}`}
                          entityName={"other_sources"}
                          trackUrl={source.url}
                          trackName={source.name}
                          trackCategory={category.name}
                          trackInfo={source.description}
                          trackId={source.id}
                          trackCover={source.imageUrl}
                          categoryId={category.id.toString()}
                          // Pass the preloaded duration here if available
                          preloadedDuration={durations[source.id] ?? 0}
                        />
                      </FreeTrialLock>
                    </VStack>
                  ))
              )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

export default OtherSources;
